/**
 * useDeploymentSocketSync Hook
 *
 * React hook for synchronizing deployment state via Socket.IO.
 * Manages connection, events, and state synchronization.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import type { CreaturePlacement, MatchDeploymentStatus, SeriesRecord } from '@drawn-of-war/shared';
import { deploymentSocket } from '../services/deployment-socket';

interface UseDeploymentSocketSyncOptions {
  matchId: string;
  playerId: 'player1' | 'player2';
  enabled?: boolean;
  onStateReceived?: (data: {
    player1Placements: CreaturePlacement[];
    player2Placements: CreaturePlacement[];
    deploymentStatus: MatchDeploymentStatus;
    creatureLimit: number;
    player1DisplayName?: string;
    player2DisplayName?: string;
  }) => void;
  onOpponentPlaced?: (placement: CreaturePlacement) => void;
  onOpponentRemoved?: (creatureId: string) => void;
  onOpponentUpdated?: (placements: CreaturePlacement[]) => void;
  onStatusChanged?: (status: MatchDeploymentStatus) => void;
  onCombatStarted?: (data: { matchId: string }) => void;
  onRoundReset?: (data: {
    matchId: string;
    roundNumber: number;
    seriesRecord: SeriesRecord;
    deploymentStatus: MatchDeploymentStatus;
    creatureLimit: number;
  }) => void;
  onOpponentLeft?: (playerId: 'player1' | 'player2') => void;
  onError?: (message: string, code?: string) => void;
}

interface DeploymentSocketState {
  isConnected: boolean;
  opponentConnected: boolean;
  opponentPlayerId: 'player1' | 'player2' | null;
  error: string | null;
  player1DisplayName: string | null;
  player2DisplayName: string | null;
}

export function useDeploymentSocketSync(options: UseDeploymentSocketSyncOptions) {
  const {
    matchId,
    playerId,
    enabled = true,
    onStateReceived,
    onOpponentPlaced,
    onOpponentRemoved,
    onOpponentUpdated,
    onStatusChanged,
    onCombatStarted,
    onRoundReset,
    onOpponentLeft,
    onError
  } = options;

  const [state, setState] = useState<DeploymentSocketState>({
    isConnected: false,
    opponentConnected: false,
    opponentPlayerId: null,
    error: null,
    player1DisplayName: null,
    player2DisplayName: null
  });

  // Determine opponent player ID
  const opponentPlayerId = playerId === 'player1' ? 'player2' : 'player1';

  // Use refs to avoid dependency issues with callbacks
  const callbacksRef = useRef({
    onStateReceived,
    onOpponentPlaced,
    onOpponentRemoved,
    onOpponentUpdated,
    onStatusChanged,
    onCombatStarted,
    onRoundReset,
    onOpponentLeft,
    onError
  });

  // Update refs on every render
  useEffect(() => {
    callbacksRef.current = {
      onStateReceived,
      onOpponentPlaced,
      onOpponentRemoved,
      onOpponentUpdated,
      onStatusChanged,
      onCombatStarted,
      onRoundReset,
      onOpponentLeft,
      onError
    };
  });

  // Connect and setup event listeners
  useEffect(() => {
    if (!enabled) {
      return;
    }

    console.log(`[useDeploymentSocketSync] Connecting to match ${matchId} as ${playerId}`);

    // Connect to socket (async) - MUST await before setting up handlers
    // because onState() does nothing if socket is null
    deploymentSocket.connect(matchId, playerId)
      .then((initialState) => {
        console.log('[useDeploymentSocketSync] Connection successful, setting up event handlers');

        // NOW set isConnected since connect() succeeded (which means deployment:state was received)
        // Also extract player display names from the initial state
        setState((prev) => ({
          ...prev,
          isConnected: true,
          error: null,
          player1DisplayName: initialState?.player1DisplayName || null,
          player2DisplayName: initialState?.player2DisplayName || null
        }));

        // CRITICAL: Call onStateReceived with the initial state returned from connect()
        // This ensures the initial state is processed even though connect() consumed the first deployment:state event
        if (initialState) {
          console.log('[useDeploymentSocketSync] Processing initial state from connect()', {
            player1DisplayName: initialState.player1DisplayName,
            player2DisplayName: initialState.player2DisplayName
          });
          callbacksRef.current.onStateReceived?.(initialState);
        }

        // Handle subsequent state updates
        deploymentSocket.onState((data) => {
          console.log('[useDeploymentSocketSync] Received state update');
          callbacksRef.current.onStateReceived?.(data);
        });

        // Handle opponent placed
        deploymentSocket.onOpponentPlaced((data) => {
          if (data.playerId === opponentPlayerId) {
            console.log('[useDeploymentSocketSync] Opponent placed creature');
            callbacksRef.current.onOpponentPlaced?.(data.placement);
          }
        });

        // Handle opponent removed
        deploymentSocket.onOpponentRemoved((data) => {
          if (data.playerId === opponentPlayerId) {
            console.log('[useDeploymentSocketSync] Opponent removed creature');
            callbacksRef.current.onOpponentRemoved?.(data.creatureId);
          }
        });

        // Handle opponent updated
        deploymentSocket.onOpponentUpdated((data) => {
          if (data.playerId === opponentPlayerId) {
            console.log('[useDeploymentSocketSync] Opponent updated placements');
            callbacksRef.current.onOpponentUpdated?.(data.placements);
          }
        });

        // Handle status changed
        deploymentSocket.onStatusChanged((status) => {
          console.log('[useDeploymentSocketSync] Deployment status changed');
          callbacksRef.current.onStatusChanged?.(status);
        });

        // Handle errors
        deploymentSocket.onError((data) => {
          console.error('[useDeploymentSocketSync] Error:', data.message);
          setState((prev) => ({ ...prev, error: data.message }));
          callbacksRef.current.onError?.(data.message, data.code);
        });

        // Handle opponent connected
        deploymentSocket.onOpponentConnected((data) => {
          if (data.playerId === opponentPlayerId) {
            console.log('[useDeploymentSocketSync] Opponent connected');
            setState((prev) => ({
              ...prev,
              opponentConnected: true,
              opponentPlayerId: data.playerId
            }));
          }
        });

        // Handle opponent disconnected
        deploymentSocket.onOpponentDisconnected((data) => {
          if (data.playerId === opponentPlayerId) {
            console.log('[useDeploymentSocketSync] Opponent disconnected');
            setState((prev) => ({
              ...prev,
              opponentConnected: false
            }));
          }
        });

        // Handle combat started
        deploymentSocket.onCombatStarted((data) => {
          console.log('[useDeploymentSocketSync] Combat started for match:', data.matchId);
          callbacksRef.current.onCombatStarted?.(data);
        });

        // Handle round reset (Fight Again)
        deploymentSocket.onRoundReset((data) => {
          console.log('[useDeploymentSocketSync] Round reset for match:', data.matchId, 'Round:', data.roundNumber);
          callbacksRef.current.onRoundReset?.(data);
        });

        // Handle opponent left
        deploymentSocket.onOpponentLeft((data) => {
          console.log('[useDeploymentSocketSync] Opponent left:', data.playerId);
          setState((prev) => ({
            ...prev,
            opponentConnected: false
          }));
          callbacksRef.current.onOpponentLeft?.(data.playerId);
        });
      })
      .catch((error) => {
        console.error('[useDeploymentSocketSync] Connection failed:', error);
        setState((prev) => ({ ...prev, error: error.message, isConnected: false }));
      });

    // Cleanup on unmount - fully disconnect to ensure clean state for next mount
    return () => {
      console.log('[useDeploymentSocketSync] Cleaning up - disconnecting socket');
      deploymentSocket.disconnect();
    };
  }, [
    matchId,
    playerId,
    enabled,
    opponentPlayerId
  ]);

  // Emit placement
  const emitPlacement = useCallback((placement: CreaturePlacement) => {
    console.log('[useDeploymentSocketSync] Emitting placement:', {
      creatureName: placement.creature.name,
      hex: placement.hex,
      isSocketConnected: deploymentSocket.isConnected
    });
    deploymentSocket.placeCreature(placement);
  }, []);

  // Emit removal
  const emitRemoval = useCallback((creatureId: string) => {
    deploymentSocket.removeCreature(creatureId);
  }, []);

  // Emit bulk update
  const emitUpdate = useCallback((placements: CreaturePlacement[]) => {
    deploymentSocket.updatePlacements(placements);
  }, []);

  // Emit ready
  const emitReady = useCallback(() => {
    console.log('[useDeploymentSocketSync] Emitting ready, socket connected:', deploymentSocket.isConnected);
    deploymentSocket.markReady();
  }, []);

  // Emit unready
  const emitUnready = useCallback(() => {
    deploymentSocket.markUnready();
  }, []);

  // Request rematch (Fight Again)
  const emitRequestRematch = useCallback(() => {
    console.log('[useDeploymentSocketSync] Requesting rematch');
    deploymentSocket.requestRematch();
  }, []);

  // Leave match
  const emitLeaveMatch = useCallback(() => {
    console.log('[useDeploymentSocketSync] Leaving match');
    deploymentSocket.leaveMatch();
  }, []);

  return {
    ...state,
    emitPlacement,
    emitRemoval,
    emitUpdate,
    emitReady,
    emitUnready,
    emitRequestRematch,
    emitLeaveMatch
  };
}
